import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Skeleton } from "@/components/ui/Skeleton";
import { ReadingText } from "@/components/reading/ReadingText";
import { ReaderSettings } from "@/components/reading/ReaderSettings";
import { WordPopover } from "@/components/reading/WordPopover";
import { SelectionToolbar } from "@/components/reading/SelectionToolbar";
import { FetchLinguisticDataButton } from "@/components/reading/FetchLinguisticDataButton";
import { TranslationPopover } from "@/components/reading/TranslationPopover";
import { SentenceAnalyzer } from "@/components/reading/sentenceAnalyzer/SentenceAnalyzer";
import { AddCustomCardModal } from "@/components/vocabulary/AddCustomCardModal";
import { AddPhraseModal } from "@/components/vocabulary/AddPhraseModal";
import { fetchReading } from "@/api/readings";
import { fetchLanguage } from "@/api/languages";
import { fetchWordCloud } from "@/api/words";
import { fetchCustomFlashcards } from "@/api/customFlashcards";
import { useResource } from "@/hooks/useResource";
import { useReaderSettings } from "@/hooks/useReaderSettings";
import { useWordSelection } from "@/hooks/useWordSelection";
import { useWordLookup } from "@/hooks/useWordLookup";
import { useTranslation } from "@/hooks/useTranslation";
import { useSentenceAnalysis } from "@/hooks/useSentenceAnalysis";
import { ENRICHMENT_SUPPORTED_ISO_CODES } from "@/types/reading";
import { cn } from "@/utils/cn";
import type { CustomFlashcard } from "@/types/customFlashcard";
import type { SharedProps } from "@/types";
import { focusRing, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'
import { PageContainer } from '@/components/layout/PageContainer'
import { CefrBadge } from '@/components/ui/CefrBadge'
import { GrammarTags } from '@/components/ui/GrammarTags'
import { useReadingShift } from '@/hooks/useReadingShift'
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage";

const SHOW_CUSTOM_KEY = "show_custom_words";

interface Props {
  id: number;
}

export default function ReadingsShow() {
  const { id, auth } = usePage().props as unknown as SharedProps & Props;

  const { data, loading } = useResource(
    () =>
      fetchReading(id).then((res) => {
        const reading = res.data.data;
        return fetchLanguage(reading.language_id).then((lres) => ({
          reading,
          language: lres.data.data,
        }));
      }),
    [id],
  );
  const reading = data?.reading ?? null;
  const language = data?.language ?? null;
  useCurrentLanguage(reading?.language_id);

  const [trackedLemmas, setTrackedLemmas] = useState<Set<string>>(new Set());
  const [customCards, setCustomCards] = useState<CustomFlashcard[]>([]);
  const [showCustom, setShowCustom] = useState(
    () => localStorage.getItem(SHOW_CUSTOM_KEY) !== "false",
  );
  const [customDraft, setCustomDraft] = useState<{ lemma: string; sentence: string } | null>(null);
  const [phraseDraft, setPhraseDraft] = useState<string | null>(null);
  const readingRef = useRef<HTMLDivElement>(null);
  /** The column that slides aside for the word panel on a wide screen. */
  const passageRef = useRef<HTMLDivElement>(null);
  const reader = useReaderSettings();

  const { selection, clearSelection } = useWordSelection(readingRef);
  const lookup = useWordLookup(reading?.language_id);
  const translation = useTranslation(reading?.language_id);
  const analysis = useSentenceAnalysis(language?.iso_code);

  useReadingShift(lookup.anchorRect !== null, passageRef);

  // Restore scroll position when returning from a word detail page.
  useEffect(() => {
    if (loading || !reading) return;
    const stored = sessionStorage.getItem("reading_scroll_restore");
    if (stored) {
      const scrollY = JSON.parse(stored) as number;
      sessionStorage.removeItem("reading_scroll_restore");
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    }
  }, [loading, reading]);

  // The set of already-known lemmas/forms so the text can mark them.
  const loadTrackedLemmas = useCallback(() => {
    if (!reading) return;
    fetchWordCloud(reading.language_id).then((res) => {
      const known = new Set<string>();
      res.data.data.forEach((e) => {
        known.add(e.lemma.toLowerCase());
        e.forms.forEach((f) => known.add(f.toLowerCase()));
      });
      setTrackedLemmas(known);
    });
  }, [reading?.language_id]);

  useEffect(() => {
    loadTrackedLemmas();
  }, [loadTrackedLemmas]);

  const loadCustomCards = useCallback(() => {
    if (!reading) return;
    fetchCustomFlashcards(reading.language_id)
      .then((res) => setCustomCards(res.data.data))
      .catch(() => setCustomCards([]));
  }, [reading?.language_id]);

  useEffect(() => {
    loadCustomCards();
  }, [loadCustomCards]);

  // Custom word cards only — phrases are multi-word and don't map to word tokens.
  const customWordCards = useMemo(
    () => customCards.filter((card) => card.card_type === "word"),
    [customCards],
  );

  // Custom-word surface forms, merged into the highlight set when the toggle is on.
  const highlightedLemmas = useMemo(() => {
    if (!showCustom || customWordCards.length === 0) return trackedLemmas;
    const merged = new Set(trackedLemmas);
    customWordCards.forEach((card) => {
      merged.add(card.lemma.toLowerCase());
      card.tracked_forms.forEach((f) => merged.add(f.toLowerCase()));
    });
    return merged;
  }, [trackedLemmas, customWordCards, showCustom]);

  // The user's own card matching the clicked word, if any.
  const matchedCustomCard = useMemo(() => {
    const clicked = lookup.word.toLowerCase();
    if (!clicked) return null;
    return (
      customWordCards.find(
        (card) =>
          card.lemma.toLowerCase() === clicked ||
          card.tracked_forms.some((f) => f.toLowerCase() === clicked),
      ) ?? null
    );
  }, [customWordCards, lookup.word]);

  const toggleShowCustom = () => {
    setShowCustom((prev) => {
      localStorage.setItem(SHOW_CUSTOM_KEY, String(!prev));
      return !prev;
    });
  };

  // Close the word popover when the reader starts a text selection.
  useEffect(() => {
    if (selection) lookup.close();
  }, [selection, lookup.close]);

  // Mark the clicked form at once, then refetch to underline the word's other inflections.
  const handleTracked = (lemma: string, surface: string) => {
    setTrackedLemmas((prev) => new Set([...prev, surface]));
    lookup.markTracked(lemma, surface);
    loadTrackedLemmas();
  };

  // Untracking drops every form of the word, so refetch rather than patch locally.
  const handleUntracked = (lemma: string, surface: string) => {
    lookup.markTracked(lemma, surface, false);
    loadTrackedLemmas();
  };

  const handleTranslate = () => {
    if (!selection) return;
    translation.translate(selection.text, selection.rect);
    clearSelection();
  };

  const handleSavePhrase = () => {
    if (!selection) return;
    setPhraseDraft(selection.text);
    clearSelection();
  };

  const handleVisualize = () => {
    if (!selection) return;
    analysis.visualize(selection.text);
    clearSelection();
  };

  return (
    <AppLayout>
      <PageContainer width="prose" className="py-10 sm:py-14">
        {/* Everything that moves aside for the word panel. The floating surfaces stay
            outside it — a transform here would capture their fixed positioning. */}
        <div ref={passageRef}>
        {loading && <ReadingSkeleton />}

        {!loading && reading && (
          <>
            <div className="animate-enter relative z-30 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <Breadcrumbs
                items={[
                  ...(language
                    ? [{ label: language.name, href: `/languages/${reading.language_id}` }]
                    : []),
                  { label: reading.title },
                ]}
              />

              <div className="flex items-center gap-2 ml-auto">
                {auth.user?.role === "admin" && (
                  <>
                    <Link
                      href={`/readings/${reading.id}/edit`}
                      className="shrink-0 inline-flex items-center min-h-11 px-3 rounded font-mono text-xs uppercase tracking-widest text-accent hover:bg-surface-bg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                    >
                      Edit
                    </Link>
                    <FetchLinguisticDataButton
                      readingId={reading.id}
                      supported={
                        language !== null &&
                        ENRICHMENT_SUPPORTED_ISO_CODES.includes(language.iso_code)
                      }
                    />
                  </>
                )}
                <ReaderSettings
                  settings={reader.settings}
                  setSize={reader.setSize}
                  setLeading={reader.setLeading}
                />
              </div>
            </div>

            <div className="animate-enter animate-enter-d1 mt-8 mb-10">
              <div className="flex items-center justify-between gap-4 mb-4">
                <CefrBadge level={reading.difficulty_level} withLabel />
                {customWordCards.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleShowCustom}
                    aria-pressed={showCustom}
                    className={cn(
                      "inline-flex items-center min-h-11 text-sm font-normal px-4 rounded-full transition-colors duration-200",
                      focusRing,
                      showCustom ? segmentedSelected : segmentedRest,
                    )}
                  >
                    Custom words {showCustom ? "on" : "off"}
                  </button>
                )}
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl font-medium text-heading">
                {reading.title}
              </h1>
              {reading.themes.length > 0 && (
                <div className="mt-4">
                  <GrammarTags tags={reading.themes.map((label) => ({ label }))} size="md" />
                </div>
              )}
            </div>
          </>
        )}

        {/* readingRef must always be in the DOM so the selection hook
            can register its event listeners on first mount */}
        <div ref={readingRef} className={!loading && reading?.body ? "animate-enter animate-enter-d2" : ""}>
          {!loading && reading?.body && (
            <ReadingText
              html={reading.body}
              onWordClick={(word, sentence, _occurrence, rect) => lookup.open(word, sentence, rect)}
              trackedLemmas={highlightedLemmas}
              settings={reader.settings}
              lookupOpen={lookup.anchorRect !== null}
            />
          )}
        </div>
        </div>

        {!loading && reading && (
          <>
            <WordPopover
              clickedWord={lookup.word}
              sentence={lookup.sentence}
              result={lookup.result}
              loading={lookup.loading}
              anchorRect={lookup.anchorRect}
              languageId={reading.language_id}
              readingId={reading.id}
              readingTitle={reading.title}
              customCard={matchedCustomCard}
              onAddCustom={() => {
                setCustomDraft({ lemma: lookup.word, sentence: lookup.sentence });
                lookup.close();
              }}
              onClose={lookup.close}
              onTracked={handleTracked}
              onUntracked={handleUntracked}
            />

            {customDraft && (
              <AddCustomCardModal
                languageId={reading.language_id}
                initial={{ lemma: customDraft.lemma, example_sentence: customDraft.sentence }}
                trackedForm={customDraft.lemma || undefined}
                open
                onClose={() => setCustomDraft(null)}
                onCreated={loadCustomCards}
              />
            )}

            {selection && !lookup.anchorRect && !translation.rect && (
              <SelectionToolbar
                rect={selection.rect}
                onTranslate={handleTranslate}
                onVisualize={handleVisualize}
                onSavePhrase={handleSavePhrase}
              />
            )}

            {phraseDraft !== null && (
              <AddPhraseModal
                languageId={reading.language_id}
                initialPhrase={phraseDraft}
                autoTranslate
                onClose={() => setPhraseDraft(null)}
                onCreated={loadCustomCards}
              />
            )}

            <TranslationPopover
              text={translation.text}
              translation={translation.result}
              loading={translation.loading}
              anchorRect={translation.rect}
              onClose={translation.close}
            />

            <SentenceAnalyzer
              open={analysis.isOpen}
              onClose={analysis.close}
              sentence={analysis.sentence}
              analysis={analysis.analysis}
              loading={analysis.loading}
            />
          </>
        )}
      </PageContainer>
    </AppLayout>
  );
}

/** Title bar plus a paragraph of lines, so the passage never appears as an empty gap. */
function ReadingSkeleton() {
  return (
    <div data-testid="reading-skeleton" aria-hidden="true">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-10 h-6 w-20 rounded-full" />
      <Skeleton className="mt-4 h-10 w-3/4" />
      <div className="mt-10 max-w-[70ch] space-y-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Skeleton key={i} line className={cn("h-5", i % 4 === 3 ? "w-2/3" : "w-full")} />
        ))}
      </div>
    </div>
  );
}
